import { createContext, type JSX, useContext, useMemo, useState } from 'react';
import type { Entitlement } from '@frontegg/nextjs';
import {
  FronteggFeatureKeys,
  FronteggPermissionKeys,
  UserEntitlementsContextType,
  UserEntitlementsProviderProps,
} from './userEntitlementsContext.types';
import { lazyWithRetry } from '../utils/lazyWithRetry';

/**
 * Calls Frontegg entitlement hooks and pushes values into the parent provider.
 * Loaded asynchronously so `@frontegg/nextjs` is not in the shell chunk for unauthenticated users.
 */
const AuthenticatedEntitlementsUpdater = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "userEntitlementsAuthenticated" */
      './userEntitlementsAuthenticated'
    ).then((mod) => ({
      default: mod.AuthenticatedEntitlementsUpdater,
    })),
  'AuthenticatedEntitlementsUpdater',
);

export const UserEntitlementsContext =
  createContext<UserEntitlementsContextType | null>(null);

export const useUserEntitlementsContext = (): UserEntitlementsContextType => {
  const context = useContext(UserEntitlementsContext);
  if (!context) {
    throw new Error(
      'useUserEntitlementsContext must be used within a UserEntitlementsProvider',
    );
  }
  return context;
};

// Preferred consumer API: thin hook wrappers over the context getters.
// Consumers should not destructure the getters from `useUserEntitlementsContext`
// directly — the wrappers exist so the React Compiler can verify hook usage.
export const useFeatureEntitlement = (
  featureKey: FronteggFeatureKeys,
): Entitlement => {
  const { getFeatureEntitlement } = useUserEntitlementsContext();
  return getFeatureEntitlement(featureKey);
};

export const usePermissionEntitlement = (
  permissionKey: FronteggPermissionKeys,
): Entitlement => {
  const { getPermissionEntitlement } = useUserEntitlementsContext();
  return getPermissionEntitlement(permissionKey);
};

const NOT_ENTITLED: Entitlement = {
  isEntitled: false,
  justification: undefined,
};

// Patient branch: must not import anything from `@frontegg/nextjs` at runtime
// (PROD-7582). Returning a stub instead of throwing lets shared components
// render in both authenticated and patient trees.
const usePatientEntitlementsContext = (): UserEntitlementsContextType => {
  return useMemo(
    () => ({
      plan: undefined,
      isPaidUser: false,
      userPlanType: undefined,
      isLoading: false,
      isSuccess: true,
      isUpgradeButtonVisible: false,
      hasPaidSubscription: false,
      isTrustedCustomer: false,
      getFeatureEntitlement: () => NOT_ENTITLED,
      getPermissionEntitlement: () => NOT_ENTITLED,
    }),
    [],
  );
};

// One provider for both auth states. The authenticated updater is mounted as
// a sibling so children don't remount on auth flips (PROD-3991), and is
// conditional so patient bundles never load Frontegg code (PROD-7582).
export const UserEntitlementsProvider = ({
  children,
  isAuthenticated,
}: UserEntitlementsProviderProps): JSX.Element => {
  const patientContextValue = usePatientEntitlementsContext();

  const [authenticatedContextValue, setAuthenticatedContextValue] =
    useState<UserEntitlementsContextType>(patientContextValue);

  const contextValue = isAuthenticated
    ? authenticatedContextValue
    : patientContextValue;

  return (
    <UserEntitlementsContext.Provider value={contextValue}>
      {isAuthenticated && (
        <AuthenticatedEntitlementsUpdater
          onUpdate={setAuthenticatedContextValue}
        />
      )}
      {children}
    </UserEntitlementsContext.Provider>
  );
};
